import { Box, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useState } from 'react';
import ProductStepper from '../../../components/stepper/ProductStepper';
import Hertex from '../../../assets/logos/Hertex_Icon.jpg';
import PinterestSaveButton from './PinterestSaveButton';

const ImageSection = ({ productListData, currentIndex }: any) => {
  const [activeImageStep, setActiveImageStep] = useState(0);
  const [isImageHovered, setIsImageHovered] = useState(false);
  const [isTapped, setIsTapped] = useState(false);

  // 🔥 ADDED: Helper function to get properties from either structure
  const getProperty = (item: any, prop: string): any => {
    if (item && item[prop] !== undefined) {
      return item[prop];
    }
    if (item?.StockMaster && item.StockMaster[prop] !== undefined) {
      return item.StockMaster[prop];
    }
    if (item?.Product && item.Product[prop] !== undefined) {
      return item.Product[prop];
    }
    return undefined;
  };

  // 🔥 UPDATED: Get current product and its properties
  const currentProduct = productListData[currentIndex];
  const additionalImages = getProperty(currentProduct, 'additionalImages') || [];
  const primaryImageUrl = getProperty(currentProduct, 'primaryImageUrl');

  const next = () => {
    setActiveImageStep((prevActiveImageStep) => {
      const lastActiveImageStep = additionalImages.length;
      return prevActiveImageStep >= lastActiveImageStep
        ? 0
        : prevActiveImageStep + 1;
    });
  };

  const previous = () => {
    setActiveImageStep((prevActiveImageStep) => {
      const lastActiveImageStep = additionalImages.length;
      return prevActiveImageStep <= 0
        ? lastActiveImageStep
        : prevActiveImageStep - 1;
    });
  };

  // 🔥 UPDATED: Build image array with fallback
  const allImages = primaryImageUrl ? [
    primaryImageUrl,
    ...additionalImages.map((img: any) => img.image_data || img)
  ] : [];

  const displayedImage = allImages.length > 0
    ? allImages[activeImageStep] || primaryImageUrl || Hertex
    : Hertex;

  return (
    <Box
      sx={{
        flex: 0,
        width: '100%',
        position: 'relative',
        pl: { xs: '0', lg: '50px' },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: { xs: '100%', lg: '585px' },
          height: { xs: '300px', lg: '600px' },
          backgroundColor: '#333',
        }}
      >
        {/* Interactive Image Wrapper for Hover Listeners */}
        <Box
          sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          data-product-image="true"
          onMouseEnter={() => setIsImageHovered(true)}
          onMouseLeave={() => setIsImageHovered(false)}
          onTouchStart={() => {
            setIsTapped(true);
            setTimeout(() => setIsTapped(false), 3000);
          }}
        >
          <img
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              backgroundColor: '#fff',
            }}
            src={displayedImage}
            alt="Product"
            data-pin-nopin="true"
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
          />

          {/* Conditional rendering of the button matching the left-aligned style */}
          {displayedImage && displayedImage !== Hertex && (
            <PinterestSaveButton
              imageUrl={displayedImage}
              description={
                getProperty(currentProduct, 'description_1') ||
                getProperty(currentProduct, 'design') ||
                'Hertex product'
              }
              visible={isImageHovered}
              tapped={isTapped}
            />
          )}
        </Box>

        {/* 🔥 UPDATED: Navigation controls with safety check */}
        <Box
          sx={{
            position: 'absolute',
            top: { xs: '15%', lg: '30%' },
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: '150px',
              pointerEvents: 'auto',
              zIndex: 2,
            }}
          >
            {additionalImages.length > 0 && (
              <>
                <ChevronLeftIcon
                  onClick={previous}
                  sx={{
                    ml: { xs: 4, lg: 4 },
                    cursor: 'pointer',
                    fontSize: '2rem',
                    background: 'rgba(255, 255, 255, 0.80)',
                    borderRadius: '4px',
                    color: '#000',
                  }}
                />

                <ProductStepper
                  length={additionalImages.length + 1}
                  currentIndex={activeImageStep}
                />

                <ChevronRightIcon
                  onClick={next}
                  sx={{
                    mr: { xs: 4, lg: 4 },
                    cursor: 'pointer',
                    fontSize: '2rem',
                    background: 'rgba(255, 255, 255, 0.80)',
                    borderRadius: '4px',
                    color: '#000',
                  }}
                />
              </>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          position: 'relative',
        }}
        mt={3}
        mb={2}
      >
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: '300',
            lineHeight: '16px',
          }}
        >
          Due to variations in computer screens, we cannot guarantee
          that colours shown here are truly representative of our
          products. Please visit a{' '}
          <a
            href='/store-locator'
            target='_blank'
            style={{
              color: '#000',
              fontWeight: '600',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
            rel='noreferrer'
          >
            Hertex showroom
          </a>{' '}
          to view samples
        </Typography>
      </Box>
    </Box>
  );
};

export default ImageSection;